import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '../../../components/navbar/ui/Navbar';
import Footer from '../../../components/footer/ui/Footer';
import ScrollToTop from '../../../components/scroll-to-top/ui/ScrollToTop';
import SEO from '../../../components/seo/SEO';
import FormDialog from '../../../components/form-dialog/ui/FormDialog';
import { useLanguage } from '../../../../context/LanguageContext';
import { Credentials } from '../../../../assets/features/Credentials';
import { submitBudgetForm } from '../../../components/email/service/appointmentFormHandler';
import budgetBgImage from '../../../../assets/logo/budget/budget_1.webp';
import budgetImage from '../../../../assets/logo/budget/budget_2.webp';
import { seoData } from '../../../../data/seoData';
import { trackPhoneClick } from '../../../components/email/utils/trackingUtils';
import '../style/budget.css';

// Maps ?service= URL params (PT slugs used across the site, e.g. /orcamento?service=pneus)
// to the form's serviceType option values (A5).
const SERVICE_PARAM_TO_TYPE: Record<string, string> = {
    'revisao': 'revision',
    'pneus': 'tire-services',
    'pacote': 'workshop-services-pack',
    'headlight': 'headlight-restoration',
    'farois': 'headlight-restoration',
    'oleo': 'oil-change',
    'ar-condicionado': 'air-conditioning',
    'lavagem': 'washing',
    'baterias': 'battery-service',
    'travoes': 'brake-service',
    'diagnostico': 'diagnostic-test',
    'inspecao': 'inspection',
    'mecanica': 'general-mechanics',
    'alinhamento': 'alignment',
    'amortecedores': 'shock-absorbers'
};

const VALID_SERVICE_TYPES = [
    'revision', 'workshop-services-pack', 'air-conditioning', 'washing',
    'headlight-restoration', 'battery-service', 'brake-service', 'oil-change',
    'engine-repair', 'tire-services', 'diagnostic-test', 'inspection',
    'general-mechanics', 'alignment', 'shock-absorbers', 'other'
];

const BudgetPage: React.FC = () => {
    const { t, language } = useLanguage();
    const [searchParams] = useSearchParams();
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        email: '',
        phone: '',
        carMake: '',
        registrationYear: '',
        kilometers: '',
        licensePlate: '',
        serviceType: '',
        serviceDescription: '',
        tireSize: '',
        privacyPolicy: false
    });
    const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
    const [filePreviewUrls, setFilePreviewUrls] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [dialogState, setDialogState] = useState<{
        isOpen: boolean;
        type: 'success' | 'error';
        message?: string;
    }>({
        isOpen: false,
        type: 'success'
    });
    const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
    const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
    const [_recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);
    

    // Pre-select the service from ?service= (A5)
    useEffect(() => {
        const param = searchParams.get('service');
        if (!param) return;
        const mapped = SERVICE_PARAM_TO_TYPE[param] || (VALID_SERVICE_TYPES.includes(param) ? param : '');
        if (mapped) {
            setFormData(prev => ({ ...prev, serviceType: mapped }));
        }
    }, [searchParams]);

    useEffect(() => {
        // Scroll to top on mount
        window.scrollTo(0, 0);

        // Debug: Check if reCAPTCHA script is loaded
        
        const checkRecaptcha = () => {
            if (typeof window.grecaptcha !== 'undefined') {
                setRecaptchaLoaded(true);
                
                window.grecaptcha.ready(() => {
                    
                    // Manually render the reCAPTCHA widget
                    setTimeout(() => {
                        const recaptchaContainer = document.querySelector('.g-recaptcha');
                        if (recaptchaContainer && !recaptchaContainer.querySelector('iframe')) {
                            try {
                                const widgetId = window.grecaptcha.render(recaptchaContainer as HTMLElement, {
                                    'sitekey': Credentials.RECAPTCHA_SITE_KEY,
                                    'theme': 'light',
                                    'size': 'normal'
                                });
                                setRecaptchaWidgetId(widgetId);
                                
                                // Verify rendering
                                setTimeout(() => {
                                    const iframe = recaptchaContainer.querySelector('iframe');
                                    if (iframe) {
                                    } else {
                                    }
                                }, 500);
                            } catch (_) {
                                setRecaptchaError('Failed to render reCAPTCHA widget');
                            }
                        } else if (recaptchaContainer && recaptchaContainer.querySelector('iframe')) {
                        } else {
                        }
                    }, 500);
                });
            } else {
            }
        };

        checkRecaptcha();

        let attempts = 0;
        const maxAttempts = 20;
        const checkInterval = setInterval(() => {
            attempts++;
            if (typeof window.grecaptcha !== 'undefined') {
                setRecaptchaLoaded(true);
                setRecaptchaError(null);
                clearInterval(checkInterval);
            } else if (attempts >= maxAttempts) {
                // reCAPTCHA is consent-gated: while the gate notice is shown
                // the script is intentionally absent — keep polling instead of
                // reporting a loading failure.
                if (!document.querySelector('.recaptcha-consent-note')) {
                    setRecaptchaError('reCAPTCHA failed to load. Please refresh the page.');
                    clearInterval(checkInterval);
                }
            }
        }, 500);

        return () => clearInterval(checkInterval);
    }, []);

    // Additional check after component fully renders
    useEffect(() => {
        const checkWidget = () => {
            const recaptchaContainer = document.querySelector('.g-recaptcha');
            if (recaptchaContainer) {
                const iframe = recaptchaContainer.querySelector('iframe');
                if (!iframe) {
                } else {
                }
            } else {
            }
        };

        // Check immediately
        checkWidget();
        
        // Check after a delay to catch late renders
        const timeout = setTimeout(checkWidget, 2000);
        
        return () => clearTimeout(timeout);
    }, [recaptchaLoaded]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleLicensePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.toUpperCase();
        
        // Remove all non-alphanumeric characters (except dashes that will be added)
        value = value.replace(/[^A-Z0-9]/g, '');
        
        // Format as XX-XX-XX with automatic dash insertion
        let formatted = '';
        
        // First 2 characters
        if (value.length > 0) {
            formatted += value.substring(0, 2);
        }
        
        // Add first dash after 2 characters
        if (value.length > 2) {
            formatted += '-';
            // Next 2 characters
            formatted += value.substring(2, 4);
        }
        
        // Add second dash after 4 characters
        if (value.length > 4) {
            formatted += '-';
            // Last 2 characters
            formatted += value.substring(4, 6);
        }
        
        setFormData(prev => ({
            ...prev,
            licensePlate: formatted
        }));
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            // Clean up previous preview URLs
            filePreviewUrls.forEach(url => URL.revokeObjectURL(url));
            
            // Allowed file types (only JPG and PNG)
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
            
            // Validate file sizes and types
            const maxSize = 3 * 1024 * 1024; // 3MB per file
            const maxTotalSize = 3 * 1024 * 1024; // 3MB combined (photos travel as base64 attachments on both admin and confirmation emails)
            let totalSize = 0;
            const oversizedTotalFiles: File[] = [];
            const validFiles = Array.from(e.target.files).filter(file => {
                const isValidSize = file.size <= maxSize;
                const isValidType = allowedTypes.includes(file.type.toLowerCase());
                if (!isValidSize || !isValidType) return false;
                if (totalSize + file.size > maxTotalSize) {
                    oversizedTotalFiles.push(file);
                    return false;
                }
                totalSize += file.size;
                return true;
            });
            
            // Check for invalid files
            const invalidSizeFiles = Array.from(e.target.files).filter(file => file.size > maxSize);
            const invalidTypeFiles = Array.from(e.target.files).filter(file => !allowedTypes.includes(file.type.toLowerCase()));
            
            // Show appropriate error messages
            if (invalidSizeFiles.length > 0) {
                const message = language === 'pt' 
                    ? 'Alguns ficheiros excedem o limite de 3MB e foram ignorados.'
                    : 'Some files exceed the 3MB limit and were ignored.';
                alert(message);
            }
            
            if (invalidTypeFiles.length > 0) {
                const message = language === 'pt' 
                    ? 'Apenas ficheiros JPG e PNG são permitidos. Outros tipos foram ignorados.'
                    : 'Only JPG and PNG files are allowed. Other types were ignored.';
                alert(message);
            }

            if (oversizedTotalFiles.length > 0) {
                const message = language === 'pt'
                    ? 'O tamanho total das fotos não pode exceder 3MB. Algumas fotos foram ignoradas.'
                    : 'The combined photo size cannot exceed 3MB. Some photos were ignored.';
                alert(message);
            }
            
            // Create new FileList with valid files
            const dataTransfer = new DataTransfer();
            validFiles.forEach(file => dataTransfer.items.add(file));
            setSelectedFiles(dataTransfer.files);
            
            // Create preview URLs for valid files
            const previewUrls = validFiles.map(file => URL.createObjectURL(file));
            setFilePreviewUrls(previewUrls);
        }
    };
    
    // Cleanup preview URLs on unmount
    useEffect(() => {
        return () => {
            filePreviewUrls.forEach(url => URL.revokeObjectURL(url));
        };
    }, [filePreviewUrls]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        
        // Validate form
        if (!formData.name || !formData.surname || !formData.email || !formData.phone ||
            !formData.carMake || !formData.licensePlate || !formData.kilometers ||
            !formData.registrationYear || !formData.serviceType) {
            const message = language === 'pt' 
                ? 'Por favor, preencha todos os campos obrigatórios.'
                : 'Please fill in all required fields.';
            setDialogState({
                isOpen: true,
                type: 'error',
                message
            });
            return;
        }
        
        // Validate tire size when tire services is selected
        if (formData.serviceType === 'tire-services' && !formData.tireSize) {
            const message = language === 'pt'
                ? 'Por favor, indique a medida dos pneus (ex.: 205/55 R16).'
                : 'Please enter the tire size (e.g., 205/55 R16).';
            setDialogState({
                isOpen: true,
                type: 'error',
                message
            });
            return;
        }

        if (!formData.privacyPolicy) {
            const message = language === 'pt' 
                ? 'Por favor, aceite a Política de Privacidade.'
                : 'Please accept the Privacy Policy.';
            setDialogState({
                isOpen: true,
                type: 'error',
                message
            });
            return;
        }
        
        // Validate reCAPTCHA - REQUIRED
        let recaptchaResponse = '';
        if (typeof window.grecaptcha !== 'undefined') {
            try {
                recaptchaResponse = window.grecaptcha.getResponse();
            } catch (e) {
            }
        }
        
        if (!recaptchaResponse) {
            const recaptchaTextarea = document.querySelector('#g-recaptcha-response') as HTMLTextAreaElement;
            if (recaptchaTextarea) {
                recaptchaResponse = recaptchaTextarea.value || '';
            }
        }
        
        if (!recaptchaResponse || recaptchaResponse === '') {
            const message = language === 'pt' 
                ? 'Por favor, complete o reCAPTCHA.'
                : 'Please complete the reCAPTCHA.';
            setDialogState({
                isOpen: true,
                type: 'error',
                message
            });
            return;
        }
        
        setIsSubmitting(true);

        const submissionPayload = {
                name: formData.name,
                surname: formData.surname,
                email: formData.email,
                phone: formData.phone,
                carMake: formData.carMake,
                registrationYear: formData.registrationYear,
                kilometers: formData.kilometers,
                licensePlate: formData.licensePlate,
                serviceType: formData.serviceType,
                tireSize: formData.serviceType === 'tire-services' ? formData.tireSize : undefined,
                serviceDescription: formData.serviceDescription || undefined,
                photos: selectedFiles,
                form_location: 'pagina_orcamento' as const
            };

        // Optimistic UI: show success immediately, reset form, fire-and-forget the send
            setDialogState({
                isOpen: true,
                type: 'success'
            });
            // Reset form
            setFormData({
                name: '',
                surname: '',
                email: '',
                phone: '',
                carMake: '',
                registrationYear: '',
                kilometers: '',
                licensePlate: '',
                serviceType: '',
                serviceDescription: '',
                tireSize: '',
                privacyPolicy: false
            });
            setSelectedFiles(null);
            // Clean up preview URLs
            filePreviewUrls.forEach(url => URL.revokeObjectURL(url));
            setFilePreviewUrls([]);
            // Reset file input
            const fileInput = document.getElementById('photoUpload') as HTMLInputElement;
            if (fileInput) fileInput.value = '';
        if (typeof window.grecaptcha !== 'undefined') {
            try { window.grecaptcha.reset(); } catch (e) { /* noop */ }
        }
        setIsSubmitting(false);

        submitBudgetForm(submissionPayload, language, recaptchaResponse).catch((err) => {
            console.error('Background mail send failed:', err);
        });
    };

    return (
        <>
            <SEO 
                title={seoData.budget.title}
                description={seoData.budget.description}
                keywords={seoData.budget.keywords}
                canonical={seoData.budget.canonical}
            />
        <div className="budget-page-wrapper">
            <Navbar />
            
            {/* Page Header */}
            <section className="page-header">
                <div
                    className="page-header__bg"
                    style={{
                        backgroundImage: `url(${budgetBgImage})`,
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        height: '380px'
                    }}
                ></div>
                <div className="container">
                    <div className="page-header__inner">
                        <div className="page-header__img-1">
                            <img loading="lazy" 
                                src={budgetImage}
                                alt="Roady Chaves" 
                                style={{ width: '200px', height: '240px', objectFit: 'contain' }}
                            />
                        </div>
                        <h1>
                            {language === 'pt'
                                ? 'Orçamento Automóvel'
                                : 'Car Quote'}
                        </h1>
                        <div className="thm-breadcrumb__inner">
                            <ul className="thm-breadcrumb list-unstyled">
                                <li><Link to="/">{t.NAVIGATION?.start || 'Início'}</Link></li>
                                <li><span className="fas fa-angle-right"></span></li>
                                <li>{t.NAVIGATION?.budget || 'Orçamento'}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* Budget Request Page */}
            <section className="budget-page">
                <div className="container">
                    <h2 className="comment-one__title budget-page__intro-title">
                        {language === 'pt' ? 'Diga-nos o que o carro precisa' : 'Tell us what your car needs'}
                    </h2>
                    <p className="budget-page__intro-text">
                        {language === 'pt'
                            ? 'Diga-nos o que o seu carro precisa e peça o seu orçamento ao Roady Chaves, sem compromisso. Se não souber identificar o problema, descreva simplesmente o que notou: um ruído, uma luz no painel ou uma diferença na condução. A nossa equipa de oficina automóvel analisa a situação e indica-lhe a solução adequada.'
                            : 'Tell us what your car needs and request your quote from Roady Chaves, with no obligation. If you cannot identify the problem, simply describe what you noticed: a noise, a dashboard light or a difference in driving. Our car workshop team analyses the situation and points you to the right solution.'}
                    </p>
                    <div className="budget-page__form-container">
                        <div className="budget-page__form-box">
                            <form className="budget-page__form" onSubmit={handleSubmit}>
                                <div className="row">
                                    {/* Personal Information */}
                                    <div className="col-xl-12">
                                        <div className="budget-page__section-title">
                                            <h3>{language === 'pt' ? 'Informações Pessoais' : 'Personal Information'}</h3>
                                        </div>
                                    </div>
                                    
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="budget-page__input-box">
                                            <label htmlFor="name">{language === 'pt' ? 'Nome *' : 'Name *'}</label>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                placeholder={language === 'pt' ? 'Insira o seu nome' : 'Enter your name'}
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <span className="budget-page__input-icon">
                                                <i className="icon-user"></i>
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="budget-page__input-box">
                                            <label htmlFor="surname">{language === 'pt' ? 'Apelido *' : 'Surname *'}</label>
                                            <input
                                                type="text"
                                                id="surname"
                                                name="surname"
                                                placeholder={language === 'pt' ? 'Insira o seu apelido' : 'Enter your surname'}
                                                value={formData.surname}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <span className="budget-page__input-icon">
                                                <i className="icon-user"></i>
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="budget-page__input-box">
                                            <label htmlFor="email">{language === 'pt' ? 'Endereço de Email *' : 'Email Address *'}</label>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                placeholder={language === 'pt' ? 'Insira o seu email' : 'Enter your email address'}
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <span className="budget-page__input-icon">
                                                <i className="icon-email"></i>
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="budget-page__input-box">
                                            <label htmlFor="phone">{language === 'pt' ? 'Número de Telefone *' : 'Phone Number *'}</label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                name="phone"
                                                placeholder={language === 'pt' ? 'Insira o seu número de telefone' : 'Enter your phone number'}
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <span className="budget-page__input-icon">
                                                <i className="icon-phone-call"></i>
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* Vehicle Information */}
                                    <div className="col-xl-12">
                                        <div className="budget-page__section-title">
                                            <h3>{language === 'pt' ? 'Informações do Veículo' : 'Vehicle Information'}</h3>
                                        </div>
                                    </div>
                                    
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="budget-page__input-box">
                                            <label htmlFor="carMake">{language === 'pt' ? 'Marca/Modelo do Carro *' : 'Car Make/Model *'}</label>
                                            <input
                                                type="text"
                                                id="carMake"
                                                name="carMake"
                                                placeholder={language === 'pt' ? 'ex.: BMW X5, Toyota Corolla' : 'e.g., BMW X5, Toyota Corolla'}
                                                value={formData.carMake}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <span className="budget-page__input-icon">
                                                <i className="icon-car"></i>
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="budget-page__input-box">
                                            <label htmlFor="registrationYear">{language === 'pt' ? 'Ano de Matrícula *' : 'Registration Year *'}</label>
                                            <input
                                                type="number"
                                                id="registrationYear"
                                                name="registrationYear"
                                                placeholder={language === 'pt' ? 'ex.: 2020' : 'e.g., 2020'}
                                                value={formData.registrationYear}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <span className="budget-page__input-icon">
                                                <i className="icon-calendar"></i>
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="budget-page__input-box">
                                            <label htmlFor="licensePlate">{language === 'pt' ? 'Matrícula *' : 'License Plate *'}</label>
                                            <input
                                                type="text"
                                                id="licensePlate"
                                                name="licensePlate"
                                                placeholder={language === 'pt' ? 'ex.: 12-AB-CD' : 'e.g., 12-AB-CD'}
                                                value={formData.licensePlate}
                                                onChange={handleLicensePlateChange}
                                                maxLength={8}
                                                required
                                            />
                                            <span className="budget-page__input-icon">
                                                <i className="icon-car"></i>
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="col-xl-6 col-lg-6">
                                        <div className="budget-page__input-box">
                                            <label htmlFor="kilometers">{language === 'pt' ? 'Quilómetros *' : 'Kilometers *'}</label>
                                            <input
                                                type="number"
                                                id="kilometers"
                                                name="kilometers"
                                                placeholder={language === 'pt' ? 'ex.: 50000' : 'e.g., 50000'}
                                                value={formData.kilometers}
                                                onChange={handleInputChange}
                                                required
                                            />
                                            <span className="budget-page__input-icon">
                                                <i className="icon-speedometer"></i>
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* Service Selection */}
                                    <div className="col-xl-12">
                                        <div className="budget-page__section-title">
                                            <h3>{language === 'pt' ? 'Seleção de Serviço' : 'Service Selection'}</h3>
                                        </div>
                                    </div>
                                    
                                    <div className="col-xl-12">
                                        <div className="budget-page__input-box">
                                            <label htmlFor="serviceType">{language === 'pt' ? 'Selecionar Serviço *' : 'Select Service *'}</label>
                                            <div className="budget-page__service-selector">
                                                <select
                                                    id="serviceType"
                                                    name="serviceType"
                                                    value={formData.serviceType}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">{language === 'pt' ? 'Escolha um serviço...' : 'Choose a service...'}</option>
                                                    <option value="revision">
                                                        {language === 'pt' ? 'Revisão Roady' : 'Roady Service (Revision)'}
                                                    </option>
                                                    <option value="workshop-services-pack">
                                                        {language === 'pt' ? 'Pack Serviços Oficina' : 'Workshop Services Pack'}
                                                    </option>
                                                    <option value="air-conditioning">
                                                        {language === 'pt' ? 'Ar Condicionado' : 'Air Conditioning'}
                                                    </option>
                                                    <option value="washing">
                                                        {language === 'pt' ? 'Lavagem' : 'Washing'}
                                                    </option>
                                                    <option value="headlight-restoration">
                                                        {language === 'pt' ? 'Restauro de Faróis' : 'Headlight Restoration'}
                                                    </option>
                                                    <option value="battery-service">
                                                        {language === 'pt' ? 'Serviço de Bateria' : 'Battery Service'}
                                                    </option>
                                                    <option value="brake-service">
                                                        {language === 'pt' ? 'Serviço de Travões' : 'Brake Service'}
                                                    </option>
                                                    <option value="oil-change">
                                                        {language === 'pt' ? 'Mudança de Óleo & Filtros' : 'Oil Change & Filters'}
                                                    </option>
                                                    <option value="engine-repair">
                                                        {language === 'pt' ? 'Reparação de Motor' : 'Engine Repair'}
                                                    </option>
                                                    <option value="tire-services">
                                                        {language === 'pt' ? 'Serviços de Pneus & Rodas' : 'Tire & Wheel Services'}
                                                    </option>
                                                    <option value="diagnostic-test">
                                                        {language === 'pt' ? 'Teste Diagnóstico' : 'Diagnostic Test'}
                                                    </option>
                                                    <option value="inspection">
                                                        {language === 'pt' ? 'Inspeção' : 'Inspection'}
                                                    </option>
                                                    <option value="general-mechanics">
                                                        {language === 'pt' ? 'Mecânica Geral' : 'General Mechanics'}
                                                    </option>
                                                    <option value="alignment">
                                                        {language === 'pt' ? 'Alinhamento' : 'Alignment'}
                                                    </option>
                                                    <option value="shock-absorbers">
                                                        {language === 'pt' ? 'Amortecedores' : 'Shock Absorbers'}
                                                    </option>
                                                    <option value="other">
                                                        {language === 'pt' ? 'Outro Serviço' : 'Other Service'}
                                                    </option>
                                                </select>
                                                <span className="budget-page__input-icon">
                                                    <i className="icon-tools"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Tire Size - visible only when tire-services is selected */}
                                    {formData.serviceType === 'tire-services' && (
                                        <div className="col-xl-12">
                                            <div className="budget-page__input-box">
                                                <label htmlFor="tireSize">
                                                    {language === 'pt' ? 'Medida dos Pneus *' : 'Tire Size *'}
                                                </label>
                                                <input
                                                    type="text"
                                                    id="tireSize"
                                                    name="tireSize"
                                                    placeholder={language === 'pt' ? 'ex.: 205/55 R16' : 'e.g., 205/55 R16'}
                                                    value={formData.tireSize}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                                <span className="budget-page__input-icon">
                                                    <i className="fas fa-tire"></i>
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Service Description */}
                                    <div className="col-xl-12">
                                        <div className="budget-page__input-box text-message-box">
                                            <label htmlFor="serviceDescription">
                                                {language === 'pt' ? 'Descrição do Serviço' : 'Service Description'}
                                            </label>
                                            <textarea
                                                id="serviceDescription"
                                                name="serviceDescription"
                                                placeholder={language === 'pt' 
                                                    ? 'Descreva em detalhe o serviço de que precisa...'
                                                    : 'Please describe the service you need in detail...'}
                                                value={formData.serviceDescription}
                                                onChange={handleInputChange}
                                            ></textarea>
                                            <span className="budget-page__input-icon">
                                                <i className="icon-comment"></i>
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* Photo Upload */}
                                    <div className="col-xl-12">
                                        <div className="budget-page__section-title">
                                            <h3>{language === 'pt' ? 'Carregar Fotografias' : 'Photo Upload'}</h3>
                                        </div>
                                    </div>
                                    
                                    <div className="col-xl-12">
                                        <div className="budget-page__input-box">
                                            <label htmlFor="photoUpload">
                                                {language === 'pt' 
                                                    ? 'Carregar Fotografias (Máx 3MB no total)'
                                                    : 'Upload Photos (Max 3MB total)'}
                                            </label>
                                            <div className="budget-page__file-upload">
                                                <input
                                                    type="file"
                                                    id="photoUpload"
                                                    name="photoUpload"
                                                    multiple
                                                    accept="image/jpeg,image/jpg,image/png"
                                                    onChange={handleFileChange}
                                                />
                                                <div className="budget-page__file-upload-area">
                                                    <div className="budget-page__file-upload-icon">
                                                        <i className="icon-mail"></i>
                                                    </div>
                                                    <p>
                                                        {selectedFiles && selectedFiles.length > 0
                                                            ? (language === 'pt' 
                                                                ? `${selectedFiles.length} ficheiro(s) selecionado(s)`
                                                                : `${selectedFiles.length} file(s) selected`)
                                                            : (language === 'pt'
                                                                ? 'Arraste e largue as fotos aqui ou clique para procurar ficheiros'
                                                                : 'Drag & drop photos here or click to browse files')
                                                        }
                                                    </p>
                                                    <small>
                                                        {language === 'pt' 
                                                            ? 'Formatos suportados: JPG, PNG (Máx 3MB no total)'
                                                            : 'Supported formats: JPG, PNG (Max 3MB total)'}
                                                    </small>
                                                </div>
                                                {selectedFiles && selectedFiles.length > 0 && (
                                                    <div style={{ 
                                                        marginTop: '15px',
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                                                        gap: '10px'
                                                    }}>
                                                        {Array.from(selectedFiles).map((file, index) => {
                                                            const imageUrl = filePreviewUrls[index];
                                                            return (
                                                                <div 
                                                                    key={index}
                                                                    style={{
                                                                        position: 'relative',
                                                                        width: '100%',
                                                                        paddingBottom: '100%',
                                                                        backgroundColor: '#f8f9fa',
                                                                        borderRadius: '5px',
                                                                        overflow: 'hidden',
                                                                        border: '1px solid #dee2e6'
                                                                    }}
                                                                >
                                                                    <img loading="lazy"
                                                                        src={imageUrl}
                                                                        alt={file.name}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            top: 0,
                                                                            left: 0,
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            objectFit: 'cover'
                                                                        }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            // Revoke URL for removed file
                                                                            URL.revokeObjectURL(filePreviewUrls[index]);
                                                                            
                                                                            const dataTransfer = new DataTransfer();
                                                                            const newPreviewUrls: string[] = [];
                                                                            Array.from(selectedFiles).forEach((f, i) => {
                                                                                if (i !== index) {
                                                                                    dataTransfer.items.add(f);
                                                                                    newPreviewUrls.push(filePreviewUrls[i]);
                                                                                }
                                                                            });
                                                                            setSelectedFiles(dataTransfer.files.length > 0 ? dataTransfer.files : null);
                                                                            setFilePreviewUrls(newPreviewUrls);
                                                                        }}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            top: '5px',
                                                                            right: '5px',
                                                                            background: 'rgba(217, 2, 2, 0.8)',
                                                                            border: 'none',
                                                                            borderRadius: '50%',
                                                                            width: '24px',
                                                                            height: '24px',
                                                                            color: 'white',
                                                                            cursor: 'pointer',
                                                                            fontSize: '16px',
                                                                            lineHeight: '1',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center'
                                                                        }}
                                                                        title={language === 'pt' ? 'Remover' : 'Remove'}
                                                                    >
                                                                        ×
                                                                    </button>
                                                                    <div style={{
                                                                        position: 'absolute',
                                                                        bottom: 0,
                                                                        left: 0,
                                                                        right: 0,
                                                                        background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
                                                                        padding: '5px',
                                                                        fontSize: '10px',
                                                                        color: 'white',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {file.name}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Privacy Policy */}
                                    <div className="col-xl-12">
                                        <div className="budget-page__section-title">
                                            <h3>{language === 'pt' ? 'Privacidade & Segurança' : 'Privacy & Security'}</h3>
                                        </div>
                                    </div>
                                    
                                    <div className="col-xl-12">
                                        <div className="budget-page__checkbox-box">
                                            <div className="budget-page__checkbox">
                                                <input
                                                    type="checkbox"
                                                    id="privacyPolicy"
                                                    name="privacyPolicy"
                                                    checked={formData.privacyPolicy}
                                                    onChange={handleInputChange}
                                                    required
                                                />
                                                <label htmlFor="privacyPolicy">
                                                    <span className="budget-page__checkbox-mark"></span>
                                                    {language === 'pt' 
                                                        ? <>Aceito a <Link to="/politica-privacidade" target="_blank">Política de Privacidade</Link> e concordo com o tratamento dos meus dados pessoais *</>
                                                        : <>I accept the <Link to="/politica-privacidade" target="_blank">Privacy Policy</Link> and agree to the processing of my personal data *</>}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* reCAPTCHA and Submit Button */}
                                    <div className="col-xl-12">
                                        <div className="appointment-one__recaptcha-btn-row">
                                            <div className="appointment-one__recaptcha-box">
                                                <div className="g-recaptcha" data-sitekey={Credentials.RECAPTCHA_SITE_KEY}></div>
                                                {/* Debug info */}
                                                {!recaptchaLoaded && !recaptchaError && (
                                                    <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                                                        Loading reCAPTCHA...
                                                    </div>
                                                )}
                                                {recaptchaError && (
                                                    <div style={{ fontSize: '12px', color: '#d90202', marginTop: '5px' }}>
                                                        ⚠️ {recaptchaError}
                                        </div>
                                                )}
                                    </div>
                                            <div className="appointment-one__btn-box budget-page__btn-row">
                                            <button
                                                type="submit"
                                                className="thm-btn budget-page__btn"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting
                                                    ? (language === 'pt' ? 'Enviando...' : 'Sending...')
                                                    : (language === 'pt' ? 'Peça orçamento' : 'Request a Quote')
                                                }
                                                <span><i className="icon-next"></i></span>
                                            </button>
                                            <a
                                                href="tel:+351276249290"
                                                className="thm-btn budget-page__call-btn"
                                                onClick={() => trackPhoneClick('budget_page')}
                                            >
                                                {language === 'pt' ? 'Fale connosco' : 'Call us'}
                                                <span><i className="icon-phone-call"></i></span>
                                            </a>
                                            <p className="budget-page__btn-note">
                                                {language === 'pt'
                                                    ? 'Respondemos com rapidez e sem compromisso.'
                                                    : 'We reply quickly and with no obligation.'}
                                            </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Prefere falar connosco? (B1) */}
                    <div className="budget-page__info-card">
                    <div className="budget-page__phone-section">
                        <h2 className="budget-page__how-title">
                            {language === 'pt' ? 'Prefere falar connosco?' : 'Prefer to talk to us?'}
                        </h2>
                        <a
                            href="tel:+351276249290"
                            className="thm-btn budget-page__call-btn"
                            onClick={() => trackPhoneClick('budget_page')}
                        >
                            {language === 'pt' ? 'Fale connosco' : 'Call us'} · (+351) 276 249 290
                            <span><i className="icon-phone-call"></i></span>
                        </a>
                        <p className="budget-page__phone-note">
                            {(t as any).CONTACT?.phoneCallInfo || 'Chamada para a rede fixa nacional'}
                        </p>
                    </div>
                    </div>
                </div>
            </section>
            
            <FormDialog
                isOpen={dialogState.isOpen}
                type={dialogState.type}
                message={dialogState.message}
                onClose={() => setDialogState({ ...dialogState, isOpen: false })}
            />
            <Footer />
            <ScrollToTop />
        </div>
        </>
    );
};

export default BudgetPage;
